import { useEffect, useState } from 'react';
import { Money } from './Money';
import { cn } from '../lib/cn';
import {
  lineDiscountAmount,
  discountPctFromAmount,
  formatPct,
  clampPct,
  round2,
  type DiscountMode,
} from '../lib/discount';

/**
 * Per-line discount entry — toggle between a percentage of the line gross and a
 * flat money amount. Switching mode converts the current value so the money
 * effect stays the same. Shows the equivalent value (= Rs X / = Y%) underneath.
 */
export function DiscountInput({
  qty,
  unitPrice,
  mode,
  value,
  onChange,
}: {
  qty: number;
  unitPrice: number;
  mode: DiscountMode;
  value: number;
  onChange: (next: { mode: DiscountMode; value: number }) => void;
}): JSX.Element {
  const gross = qty * unitPrice;
  const amount = lineDiscountAmount(qty, unitPrice, mode, value);
  const effectivePct = discountPctFromAmount(qty, unitPrice, amount);

  // Plain `type="number"` silently drops a comma decimal (the everyday
  // notation in Mauritius' French locale — "4,5") instead of typing it, and a
  // fully-controlled numeric value snaps a still-being-typed "4." back to "4"
  // on every keystroke, erasing the decimal point before a second digit can
  // follow. Track the raw text separately so it free-types, and only commit
  // once it parses to a real number — never silently coerce garbage to 0.
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value, mode]);

  const setMode = (next: DiscountMode): void => {
    if (next === mode) return;
    // Convert so the resulting money discount is preserved across the switch.
    const converted =
      next === 'amount'
        ? lineDiscountAmount(qty, unitPrice, 'pct', value)
        : gross > 0
          ? round2(discountPctFromAmount(qty, unitPrice, value))
          : 0;
    onChange({ mode: next, value: converted });
  };

  const onTextChange = (raw: string): void => {
    setText(raw);
    const normalized = raw.trim().replace(',', '.');
    if (normalized === '' || normalized === '.') return; // still typing — don't commit yet
    const n = Number(normalized);
    if (!Number.isFinite(n)) return; // not a number yet — keep the last committed value
    const v = mode === 'pct' ? clampPct(n) : Math.max(0, Math.min(gross || n, n));
    onChange({ mode, value: v });
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="inline-flex items-center gap-1">
        <div className="inline-flex rounded border border-rule overflow-hidden">
          {(['pct', 'amount'] as DiscountMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                'px-1.5 py-1 text-[10px] font-medium transition',
                mode === m ? 'bg-accent text-white' : 'bg-paper-2 text-ink-3 hover:text-ink',
              )}
              aria-pressed={mode === m}
            >
              {m === 'pct' ? '%' : 'Rs'}
            </button>
          ))}
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          onBlur={() => setText(String(value))}
          className="kc-input w-16 text-right kc-mono"
        />
      </div>
      {amount > 0 && (
        <div className="text-[10px] text-ink-3">
          {mode === 'pct' ? (
            <>
              = −<Money value={amount} muted />
            </>
          ) : (
            <>= {formatPct(effectivePct)}</>
          )}
        </div>
      )}
    </div>
  );
}
